<template>
  <div class="results9-wrap">

    <div class="topbar">
      <div class="topbar-inner">
        <div class="brand">
          <div class="brand-mark"></div>
          <div class="brand-text">SEQ <small>Swiss Eating Quiz · Sustainability Report</small></div>
        </div>
        <div class="topbar-meta">SUSTAINABILITY · 09.05.2026 · ID 2841-CH</div>
      </div>
    </div>

    <div class="hero">
      <div class="hero-inner">
        <div class="hero-eyebrow">Sustainability assessment · brainstorming draft</div>
        <h1 class="hero-title">The participant's <em>plate</em>, and the planet behind it.</h1>
        <p class="hero-sub">
          What this person eats is also a question of land, water, climate, biodiversity, and labour. This report unpacks the sustainability dimensions of the reported dietary pattern across environmental, social, and governance axes. Intended as a brainstorming artefact for the SEQ team's sustainability work, not a final deliverable. The numbers here are reasonable estimates from the literature; the framing and selection of issues are open for discussion.
        </p>
      </div>
    </div>

    <div class="meta-grid">
      <div v-for="cell in metaCells" :key="cell.label" class="meta-cell">
        <div class="meta-label">{{ cell.label }}</div>
        <div class="meta-value">{{ cell.value }}</div>
      </div>
    </div>

    <div class="toc">
      <div class="toc-inner">
        <div class="toc-title">Contents of this report</div>
        <div class="toc-grid">
          <div v-for="t in tocItems" :key="t.num" class="toc-item">
            <span class="toc-num">{{ t.num }}</span>
            <span class="toc-item-title">{{ t.title }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- SECTION 1: EXECUTIVE SUMMARY -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">01 / 15</div>
          <div>
            <h2 class="section-title">Executive <em>summary</em>.</h2>
            <p class="section-lede">
              The five numbers and three sentences a reader should leave with if they only read the first page.
            </p>
          </div>
        </div>

        <div class="metric-grid-5">
          <div v-for="m in executiveMetrics" :key="m.label" class="metric">
            <div class="metric-label">{{ m.label }}</div>
            <div class="metric-value" :class="m.status">{{ m.value }} <span class="unit">{{ m.unit }}</span></div>
            <div class="metric-detail">{{ m.detail }}</div>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Three sentences</div>
          <div class="card-title">The takeaway</div>
          <p>The participant's nutrition is solid (76 / 100), but the sustainability cost of how they get there is real and uneven. Three specific factors do most of the damage: high dairy intake (carbon and land), 4 cups of coffee a day (water and tropical land use), and processed meat (3× / week, carbon and biodiversity). At the same time, three things work in the participant's favour: zero sweetened beverages (good for packaging and water), high vegetable intake (low carbon when seasonal), and water-as-drink dominance (almost zero environmental footprint).</p>
          <p>The two largest sustainability levers, mirroring the nutrition advice, point in the same direction: increase plant proteins (legumes are roughly 10× lower in carbon than equivalent dairy or meat protein) and shift refined grains to whole grains (lower processing energy, lower yield-per-input). If the participant adopted only these two changes, the diet's annual carbon footprint would drop by roughly 0.3 t CO₂e, the land-use footprint by ~250 m²·y, and the nutrition score would rise. This is one of the rare cases where sustainability and nutrition are pointing the same way.</p>
          <p>The bigger conversations are elsewhere. The coffee question, the dairy question, and the imports question are deeply embedded in Swiss culture and lifestyle. These are not individual moral failures; they are systemic patterns. A productive next step for the SEQ team is to decide which of these patterns we surface in the participant-facing report (risk of moralising) and which we keep as institutional information (useful for policy partners like schools, hospitals, canteens).</p>
        </div>
      </div>
    </section>

    <!-- SECTION 2: CARBON FOOTPRINT -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">02 / 15</div>
          <div>
            <h2 class="section-title">Carbon <em>footprint</em>.</h2>
            <p class="section-lede">
              The estimated greenhouse-gas footprint of the reported dietary pattern, annualised, broken down by food group. Based on Swiss-relevant Life-Cycle Assessment data (Agroscope, Eaternity, BAFU studies). Single-source uncertainty is roughly ±20 %; the rank order is robust.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Table 2.1 · Annual carbon footprint by food group</div>
          <div class="card-title">Approximate kg CO₂e per year, this participant's pattern</div>
          <p class="card-sub">Reference figure is the EAT-Lancet planetary diet for an adult (~1,150 kg CO₂e / year for diet alone).</p>

          <table class="data">
            <thead>
              <tr>
                <th>Food group</th>
                <th class="num">Reported / yr</th>
                <th class="num">kg CO₂e / yr</th>
                <th class="num">Share of diet</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in carbonTable" :key="row.name">
                <td class="name"><strong v-if="row.bold">{{ row.name }}</strong><template v-else>{{ row.name }}</template></td>
                <td class="num"><strong v-if="row.bold">{{ row.reported }}</strong><template v-else>{{ row.reported }}</template></td>
                <td class="num"><strong v-if="row.bold">{{ row.kg }}</strong><template v-else>{{ row.kg }}</template></td>
                <td class="num"><strong v-if="row.bold">{{ row.share }}</strong><template v-else>{{ row.share }}</template></td>
                <td>{{ row.note }}</td>
              </tr>
            </tbody>
          </table>

          <p class="card-paragraph card-paragraph--note">
            <strong>Benchmarks for context:</strong> Average Swiss diet is ~ 2.1–2.3 t CO₂e / year (BAFU). EAT-Lancet planetary diet (mostly plant, modest dairy and animal): ~ 1.15 t. Strict vegan: ~ 0.7 t. Heavy-meat Swiss male: up to 3.2 t. The participant's diet sits at the Swiss average.
          </p>
        </div>

        <div class="card">
          <div class="card-eyebrow">Why coffee stands out</div>
          <div class="card-title">A footnote worth its own paragraph</div>
          <p>Coffee is one of the highest-impact items per unit of nutritional value in any Western diet. The number above (320 kg CO₂e / year for 4 cups / day) sounds small until you compare it to "average Swiss household electricity" (~ 750 kg CO₂e / year in some cantons). One person's coffee habit can equal half the climate impact of running their entire household's lights, fridge, and electronics.</p>
          <p>The drivers are tropical deforestation for coffee cultivation, on-farm fertiliser use, processing (washing, drying), shipping, and the disproportionate energy of espresso and capsule machines. Capsules in particular roughly double the per-cup footprint compared to filter coffee. None of this is in the SEQ data; it would be a sensible v2 question.</p>
        </div>
      </div>
    </section>

    <!-- SECTION 3: LAND, WATER, BIODIVERSITY -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">03 / 15</div>
          <div>
            <h2 class="section-title">Land, water, <em>biodiversity</em>.</h2>
            <p class="section-lede">
              Carbon is not the whole story. Three other environmental axes matter at least as much when comparing diets: land use (how much land is needed to feed this person for a year), water footprint (including invisible "virtual water" from imports), and biodiversity pressure (how many species this diet displaces, particularly in tropical regions).
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Table 3.1 · Land and water by food group</div>
          <div class="card-title">Annualised resource use, per person</div>

          <table class="data">
            <thead>
              <tr>
                <th>Food group</th>
                <th class="num">Land (m²·yr)</th>
                <th class="num">Water (m³)</th>
                <th>Main driver</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in landWaterTable" :key="row.name">
                <td class="name"><strong v-if="row.bold">{{ row.name }}</strong><template v-else>{{ row.name }}</template></td>
                <td class="num"><strong v-if="row.bold">{{ row.land }}</strong><template v-else>{{ row.land }}</template></td>
                <td class="num"><strong v-if="row.bold">{{ row.water }}</strong><template v-else>{{ row.water }}</template></td>
                <td>{{ row.driver }}</td>
              </tr>
            </tbody>
          </table>

          <p class="card-paragraph card-paragraph--note">
            <strong>For context:</strong> Total productive land per Swiss resident is ~ 1,400 m². This participant's diet alone consumes more than that, the difference coming from imports. Globally, agriculture uses ~ 38 % of all habitable land; ~ 75 % of that is for animal products. The land that does not exist in Switzerland sits in Brazil, Argentina, the US, Eastern Europe.
          </p>
        </div>

        <div class="card">
          <div class="card-eyebrow">Biodiversity pressure</div>
          <div class="card-title">Where this diet bumps into other species</div>
          <p>Biodiversity loss is the planetary boundary in the worst state globally, ahead of even climate change. Food is the single largest driver: ~ 70 % of biodiversity loss in the 20th century traces to agricultural expansion. Per food group, the pressure for this participant's diet:</p>

          <table class="data table-spaced">
            <thead>
              <tr><th>Food group</th><th>Biodiversity pressure</th><th>Where</th><th>Notes</th></tr>
            </thead>
            <tbody>
              <tr v-for="row in biodiversityTable" :key="row.name">
                <td class="name">{{ row.name }}</td>
                <td><span class="pill" :class="row.pill">{{ row.pressure }}</span></td>
                <td>{{ row.where }}</td>
                <td>{{ row.note }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- SECTION 4: PLANETARY BOUNDARIES -->
    <section class="section accent">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">04 / 15</div>
          <div>
            <h2 class="section-title">Where this diet sits on the <em>planetary boundaries</em>.</h2>
            <p class="section-lede">
              The Stockholm Resilience Centre's planetary boundaries framework identifies nine processes that regulate the stability of the Earth system. Six are currently considered "crossed" globally. Food contributes to at least eight of the nine.
            </p>
          </div>
        </div>

        <div class="boundary-grid">
          <div v-for="b in planetaryBoundaries" :key="b.name" class="boundary-card" :class="b.level">
            <div class="boundary-name">{{ b.name }}</div>
            <div class="boundary-state">{{ b.state }}</div>
            <div class="boundary-body">{{ b.body }}</div>
          </div>
        </div>

        <p class="section-note">
          <strong>Interpretive note:</strong> A single person's diet cannot "cross" or "stay within" a planetary boundary; the boundaries are global. But every diet contributes a per-capita share. The point of showing them is to make visible that food choices intersect with multiple Earth-system processes, not just climate.
        </p>
      </div>
    </section>

    <!-- SECTION 5: EAT-LANCET -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">05 / 15</div>
          <div>
            <h2 class="section-title">Comparison with the <em>EAT-Lancet</em> planetary health diet.</h2>
            <p class="section-lede">
              The EAT-Lancet Commission (2019, updated 2024-2025) proposed a "planetary health diet" that is both nutritionally adequate and consistent with planetary boundaries for ten billion people. Below: how the participant's reported pattern compares to EAT-Lancet targets per food group, expressed in grams per day.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Fig. 5.1 · Reported pattern vs. EAT-Lancet</div>
          <div class="card-title">Each row shows participant intake (bar) against EAT-Lancet target (vertical line)</div>
          <p class="card-sub">Both expressed in grams per day. Bars far from the line indicate deviation from the planetary health reference.</p>

          <div class="compare-row head">
            <div>Food group</div>
            <div>0 ── 100 ── 200 ── 300 ── 400 g / day</div>
            <div>Status</div>
          </div>
          <div v-for="row in eatLancetRows" :key="row.name" class="compare-row">
            <div class="compare-name">{{ row.name }}</div>
            <div class="compare-bar-wrap">
              <div class="compare-bar" :class="row.bar" :style="{ width: row.barWidth + '%' }"></div>
              <div class="compare-ref" :style="{ left: row.refLeft + '%' }"></div>
            </div>
            <div class="compare-val">{{ row.val }}</div>
          </div>

          <p class="card-paragraph card-paragraph--note">
            <strong>Reading the chart:</strong> The reference line (black) is the EAT-Lancet target. The bar is the participant's reported intake. Where the bar matches the line, the pattern is well-aligned. The biggest planetary-health deviations are: too much red meat (~ 4.5× target), too little whole grain (~ 22 %), too little legumes (~ 20 %), and no fish. Dairy is moderately above target.
          </p>
        </div>
      </div>
    </section>

    <!-- SECTION 6: ESG MATRIX -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">06 / 15</div>
          <div>
            <h2 class="section-title">ESG <em>matrix</em>.</h2>
            <p class="section-lede">
              Environmental, Social, Governance is a framework originally for evaluating investments. Applied to a diet, it gives a structured way to talk about non-obvious dimensions: not just "is this green," but "how was it made, by whom, under what rules." The matrix below shows three rows (E, S, G) by three columns (covering different specific issues per row).
            </p>
          </div>
        </div>

        <div class="esg-matrix">
          <div></div>
          <div class="esg-header">Strong issue (1)</div>
          <div class="esg-header">Strong issue (2)</div>
          <div class="esg-header">Strong issue (3)</div>

          <template v-for="row in esgMatrix" :key="row.row">
            <div class="esg-header row">{{ row.row }}</div>
            <div v-for="cell in row.cells" :key="cell.title" class="esg-cell">
              <span class="esg-cell-rating" :class="cell.rating">{{ cell.ratingLabel }}</span>
              <div class="esg-cell-title">{{ cell.title }}</div>
              <div class="esg-cell-body">{{ cell.body }}</div>
            </div>
          </template>
        </div>

        <p class="section-note">
          <strong>How to read the matrix:</strong> Each cell is a sustainability dimension, rated for this participant's reported diet. A "concern" rating does not mean the participant is doing something wrong; it means the dimension is structurally weak in the diet as reported. The matrix is a brainstorming tool, not a scoring rubric.
        </p>
      </div>
    </section>

    <!-- SECTION 7: SUPPLY CHAIN -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">07 / 15</div>
          <div>
            <h2 class="section-title">Where does this <em>food</em> come from?</h2>
            <p class="section-lede">
              A Swiss diet looks local on the plate but rarely is in the field. The table below traces the participant's main food categories back to their origin, with the dominant sustainability issue per origin region.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Table 7.1 · Geographic supply chain</div>
          <div class="card-title">Origin and issue per food category</div>

          <div class="supply-row head">
            <div>Food category</div>
            <div>Likely origin</div>
            <div>Dominant sustainability / ethical issue</div>
            <div>Import share</div>
          </div>
          <div v-for="row in supplyChain" :key="row.category" class="supply-row">
            <div>{{ row.category }}</div>
            <div>{{ row.origin }}</div>
            <div class="supply-issue">{{ row.issue }}</div>
            <div>{{ row.importShare }}</div>
          </div>

          <p class="section-note">
            <strong>Note for the team:</strong> SEQ v1 does not capture origin or season. The information above is inferred from typical Swiss food-system patterns. A v2 question on "do you check origin labels?" or "do you eat seasonally?" would unlock much sharper sustainability assessment.
          </p>
        </div>
      </div>
    </section>

    <!-- SECTION 8: HUMAN AND LABOUR -->
    <section class="section earth">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">08 / 15</div>
          <div>
            <h2 class="section-title">The human side: <em>labour</em>, fairness, dignity.</h2>
            <p class="section-lede">
              Food is grown, harvested, processed, packed, transported, and sold by people. Many of the items on the participant's plate, especially those imported from the Global South, are produced under conditions that would be unlawful or unthinkable in Switzerland. This section makes the invisible visible.
            </p>
          </div>
        </div>

        <div v-for="card in labourCards" :key="card.title" class="card">
          <div class="card-eyebrow">{{ card.eyebrow }}</div>
          <div class="card-title">{{ card.title }}</div>
          <p v-for="(p, i) in card.paragraphs" :key="i">{{ p }}</p>
        </div>
      </div>
    </section>

    <!-- SECTION 9: ANIMAL WELFARE -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">09 / 15</div>
          <div>
            <h2 class="section-title">Animal welfare <em>dimension</em>.</h2>
            <p class="section-lede">
              A diet with substantial animal products carries an animal-welfare footprint. Swiss law (Tierschutzgesetz) is among the strictest in the world; imported animal products often come from systems where it would be illegal here.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Approximate annual animal count</div>
          <div class="card-title">Animals contributing to one year of this diet</div>
          <p>An approximation, based on average meat / dairy / egg yields per animal and the participant's reported intake. Numbers are rough but illustrate scale.</p>

          <table class="data table-spaced">
            <thead>
              <tr>
                <th>Animal</th>
                <th class="num">Approx. number / yr</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in animalWelfareTable" :key="row.name">
                <td class="name">{{ row.name }}</td>
                <td class="num">{{ row.count }}</td>
                <td>{{ row.note }}</td>
              </tr>
            </tbody>
          </table>

          <p class="card-paragraph card-paragraph--note">Domestic vs. imported origin matters enormously here. Swiss-produced chicken (~ 60 % of Swiss consumption) is raised under stricter welfare laws than EU or imported chicken. Same for pork and eggs. Beef is more mixed. Choosing Swiss origin for animal products is one of the clearest animal-welfare gains a Swiss consumer can make, even without reducing consumption.</p>
        </div>
      </div>
    </section>

    <!-- SECTION 10: SDG ALIGNMENT -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">10 / 15</div>
          <div>
            <h2 class="section-title">Alignment with the UN <em>Sustainable Development Goals</em>.</h2>
            <p class="section-lede">
              The 17 SDGs are a recognised global framework. At least eight intersect substantively with food and diet. Below: how this participant's pattern supports or undermines each.
            </p>
          </div>
        </div>

        <div class="sdg-grid">
          <div v-for="s in sdgTiles" :key="s.num" class="sdg-tile">
            <div class="sdg-num">{{ s.num }}</div>
            <div class="sdg-title">{{ s.title }}</div>
            <div class="sdg-body">{{ s.body }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 11: SWISS CONTEXT -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">11 / 15</div>
          <div>
            <h2 class="section-title">Swiss-specific <em>context</em>.</h2>
            <p class="section-lede">
              Switzerland sits at the centre of any Swiss-focused sustainability discussion in unusual ways: strict domestic regulation, very high import share, very high food waste, high consumer purchasing power. The same diet that is "average" in Switzerland looks different when contextualised.
            </p>
          </div>
        </div>

        <div class="metric-grid-4">
          <div v-for="m in swissMetrics" :key="m.label" class="metric">
            <div class="metric-label">{{ m.label }}</div>
            <div class="metric-value" :class="m.status">{{ m.value }} <span class="unit">{{ m.unit }}</span></div>
            <div class="metric-detail">{{ m.detail }}</div>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Swiss labels worth knowing</div>
          <div class="card-title">A quick reference</div>
          <table class="data">
            <thead>
              <tr><th>Label</th><th>Scope</th><th>Sustainability focus</th></tr>
            </thead>
            <tbody>
              <tr v-for="l in swissLabels" :key="l.label">
                <td class="name">{{ l.label }}</td>
                <td>{{ l.scope }}</td>
                <td>{{ l.focus }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- SECTION 12: WASTE, PACKAGING, SEASON -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">12 / 15</div>
          <div>
            <h2 class="section-title">Food waste, packaging, <em>seasonality</em>.</h2>
            <p class="section-lede">
              Three sustainability dimensions that the SEQ does not currently measure but that significantly shape a diet's impact. Each is a candidate for v2 questions.
            </p>
          </div>
        </div>

        <div class="metric-grid-3">
          <div v-for="m in wasteMetrics" :key="m.label" class="metric">
            <div class="metric-label">{{ m.label }}</div>
            <div class="metric-value" :class="m.status">{{ m.value }} <span class="unit">{{ m.unit }}</span></div>
            <div class="metric-detail">{{ m.detail }}</div>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Why these matter</div>
          <div class="card-title">A short note</div>
          <p><strong>Food waste:</strong> Up to 35 % of all food produced globally is wasted. Eliminating household food waste alone would have a climate impact larger than removing every car in Switzerland from the roads. Waste is the hidden multiplier on every other dimension.</p>
          <p><strong>Packaging:</strong> Per portion, packaging is usually a small share of total environmental impact (~ 3-8 % for most categories). But it is the most visible, and a behaviour change consumers can directly enact. Reusable bottles, refill stores, market shopping all reduce both packaging and food miles.</p>
          <p><strong>Seasonality:</strong> A locally-grown tomato in August has a footprint roughly 10× lower than a greenhouse-grown one in February. The same applies to most fruits and vegetables. Seasonal eating is one of the highest-leverage individual behaviours, with almost no nutritional downside.</p>
        </div>
      </div>
    </section>

    <!-- SECTION 13: SCENARIOS -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">13 / 15</div>
          <div>
            <h2 class="section-title">"What if" <em>scenarios</em>.</h2>
            <p class="section-lede">
              Hypothetical changes to the participant's diet and their approximate impact. The point is not to prescribe, but to surface where the largest sustainability levers are.
            </p>
          </div>
        </div>

        <div v-for="s in scenarios" :key="s.name" class="scenario-card">
          <div class="scenario-header">
            <div class="scenario-name">{{ s.name }}</div>
            <div class="scenario-delta">{{ s.delta }}</div>
            <div><span class="pill" :class="s.pill">{{ s.pillLabel }}</span></div>
          </div>
          <div class="scenario-body">{{ s.body }}</div>
        </div>
      </div>
    </section>

    <!-- SECTION 14: BRAINSTORM -->
    <section class="section accent">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">14 / 15</div>
          <div>
            <h2 class="section-title">Brainstorm prompts for the <em>team</em>.</h2>
            <p class="section-lede">
              Open questions for the SEQ sustainability team. Each is genuinely contested or unresolved. None has a clean answer.
            </p>
          </div>
        </div>

        <div class="brainstorm-grid">
          <div v-for="q in brainstorm" :key="q.title" class="brainstorm-card">
            <div class="brainstorm-eyebrow">{{ q.eyebrow }}</div>
            <div class="brainstorm-title">{{ q.title }}</div>
            <div class="brainstorm-body" v-html="q.body"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 15: CAVEATS -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">15 / 15</div>
          <div>
            <h2 class="section-title">Open questions and <em>caveats</em>.</h2>
            <p class="section-lede">
              The honest fine print. Everything we are not yet sure about.
            </p>
          </div>
        </div>

        <div v-for="c in caveats" :key="c.title" class="card">
          <div class="card-eyebrow">{{ c.eyebrow }}</div>
          <div class="card-title">{{ c.title }}</div>
          <p>{{ c.body }}</p>
        </div>

        <p class="placeholder-note">End of brainstorming document. Length is intentional. Selection of issues, framing, and tone are all open for team discussion.</p>
      </div>
    </section>

    <div class="footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <div class="brand">
            <div class="brand-mark"></div>
            <div class="brand-text">SEQ <small>Swiss Eating Quiz · Sustainability Report</small></div>
          </div>
          <p>
            The SEQ is a research instrument developed at Bern University of Applied Sciences. This sustainability brainstorming report uses LCA data from Agroscope, Eaternity, FAO, and EAT-Lancet sources. It is intended as a working document for the SEQ team and partners, not for external publication. Numbers should be verified before any public use.
          </p>
        </div>
        <div class="footer-meta">
          Brainstorming draft<br>
          v0.1 · May 2026<br>
          BFH Department of Health
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'Results9',
  data() {
    return {
      metaCells: [
        { label: 'Participant ID', value: '2841-CH (sample)' },
        { label: 'Dietary pattern', value: 'Mixed Western, dairy-positive' },
        { label: 'Nutrition score', value: '76 / 100' },
        { label: 'Sustainability score (est.)', value: '52 / 100' },
      ],
      tocItems: [
        { num: '01', title: 'Executive summary' },
        { num: '02', title: 'Carbon footprint of this diet' },
        { num: '03', title: 'Land, water, biodiversity' },
        { num: '04', title: 'Planetary boundaries' },
        { num: '05', title: 'EAT-Lancet comparison' },
        { num: '06', title: 'ESG matrix' },
        { num: '07', title: 'Where does this food come from' },
        { num: '08', title: 'Human and labour issues' },
        { num: '09', title: 'Animal welfare' },
        { num: '10', title: 'SDG alignment' },
        { num: '11', title: 'Swiss-specific context' },
        { num: '12', title: 'Food waste, packaging, season' },
        { num: '13', title: '"What if" scenarios' },
        { num: '14', title: 'Brainstorm prompts for the team' },
        { num: '15', title: 'Open questions and caveats' },
      ],
      executiveMetrics: [
        { label: 'Annual carbon footprint', value: '2.1', unit: 't CO₂e / year', status: 'warn', detail: 'Above the 1.5 t target compatible with 1.5 °C. Diet alone accounts for ~25 % of the participant\'s total footprint.' },
        { label: 'Land use', value: '1,840', unit: 'm² · y', status: 'warn', detail: 'Roughly 60 % above the EAT-Lancet planetary diet reference. Driven mainly by dairy and meat.' },
        { label: 'Water footprint', value: '1,420', unit: 'm³ / year', status: 'warn', detail: 'Green and blue water combined. Roughly 70 % is "virtual water" embedded in imported food.' },
        { label: 'Import share', value: '52', unit: '%', status: 'bad', detail: 'By weight, more than half the food comes from outside Switzerland. Hidden labour and ecology travel with it.' },
        { label: 'Sustainability score (composite)', value: '52', unit: '/ 100', status: 'warn', detail: 'Mid-range. The nutrition score is higher than the sustainability score, which is typical for a Swiss Western diet.' },
      ],
      carbonTable: [
        { name: 'Dairy', reported: '~ 110 kg dairy', kg: '560', share: '27 %', note: 'Methane from cattle is the dominant driver. Hard cheese in particular has a high footprint per kg.' },
        { name: 'Animal proteins (meat, fish, eggs)', reported: '~ 65 kg total', kg: '490', share: '23 %', note: 'Red meat (beef, pork, lamb) and processed meat carry the highest footprint. Poultry and eggs are middle-tier.' },
        { name: 'Coffee (drinks)', reported: '~ 1,460 cups / yr (4 / day)', kg: '320', share: '15 %', note: 'Coffee carries a surprisingly high footprint per cup due to tropical land use, processing, transport, and home-machine energy.' },
        { name: 'Grains and starches', reported: '~ 130 kg', kg: '240', share: '11 %', note: 'Bread, pasta, rice, potatoes. Refined-grain processing adds modest energy on top of agriculture.' },
        { name: 'Vegetables', reported: '~ 90 kg', kg: '190', share: '9 %', note: 'Greenhouse-grown vegetables (tomato out of season, peppers in winter) push this number up. Field vegetables are very low.' },
        { name: 'Fruit', reported: '~ 60 kg', kg: '160', share: '8 %', note: 'Air-freighted fruit (berries out of season, some tropical) dominates this number despite low total mass.' },
        { name: 'Plant proteins', reported: '~ 5 kg', kg: '15', share: '~ 1 %', note: 'The lowest footprint per protein in the table. The participant eats very little, which is a missed sustainability opportunity.' },
        { name: 'Other beverages (water, tea)', reported: '~ 700 L', kg: '10', share: '< 1 %', note: 'Tap water in Switzerland is near-zero footprint. Tea adds a small amount.' },
        { name: 'Total', reported: '—', kg: '~ 1,985 kg', share: '100 %', note: 'Rounded up to ~ 2.1 t CO₂e to account for hidden items (snacks, oils, condiments not in SEQ v1).', bold: true },
      ],
      landWaterTable: [
        { name: 'Dairy', land: '520', water: '200', driver: 'Pasture and feed-crop land, especially imported soybean meal for concentrate feed.' },
        { name: 'Animal proteins', land: '440', water: '280', driver: 'Beef especially land-intensive; pork and poultry less so. Feed crops drive water.' },
        { name: 'Coffee', land: '230', water: '370', driver: 'Coffee plantations in Brazil, Vietnam, Ethiopia. Mostly green water (rainfall), some blue water (irrigation).' },
        { name: 'Grains', land: '280', water: '190', driver: 'Wheat fields in Switzerland and Eastern Europe; rice from Italy or further afield.' },
        { name: 'Vegetables', land: '110', water: '140', driver: 'Open-field crops are land-efficient. Greenhouse veg is water-efficient but energy-intensive.' },
        { name: 'Fruit', land: '160', water: '220', driver: 'Orchards take long-term land. Tropical fruit (citrus, banana) carries water from elsewhere.' },
        { name: 'Plant proteins', land: '35', water: '15', driver: 'Pulses are nitrogen-fixing and water-efficient. Among the most resource-light protein sources.' },
        { name: 'Total', land: '1,775', water: '1,415', driver: 'Plus small additions from snacks, oils, sweets not measured.', bold: true },
      ],
      biodiversityTable: [
        { name: 'Beef and dairy', pill: 'bad', pressure: 'High', where: 'Amazon, Cerrado (Brazil)', note: 'Feed-soy expansion is the primary driver of Amazon deforestation. Affects jaguars, sloths, indigenous peoples.' },
        { name: 'Coffee', pill: 'warn', pressure: 'Medium-high', where: 'Latin America, Vietnam, Ethiopia', note: 'Sun-grown ("technified") coffee replaces shade-grown systems that support migratory birds and pollinators.' },
        { name: 'Fish (when consumed)', pill: 'warn', pressure: 'Medium', where: 'Oceans and aquaculture', note: 'Bycatch, overfishing, mangrove destruction for shrimp farms. Participant eats no fish, so pressure here is near zero.' },
        { name: 'Palm oil (in processed foods)', pill: 'bad', pressure: 'High', where: 'Southeast Asia', note: 'Orangutan habitat loss. Hidden in many processed foods; SEQ does not measure.' },
        { name: 'Vegetables and fruit (local)', pill: 'good', pressure: 'Low', where: 'Switzerland', note: 'Local field-grown produce can support pollinators if grown with extensive methods (IP-Suisse, Bio).' },
        { name: 'Legumes', pill: 'good', pressure: 'Low', where: 'Various', note: 'Nitrogen-fixing crops can improve soil biodiversity. Participant eats very few.' },
      ],
      planetaryBoundaries: [
        { level: 'exceeded', name: 'Climate change', state: 'Exceeded globally', body: 'This participant\'s 2.1 t CO₂e / year from diet alone exceeds a per-person budget for 1.5 °C. Personal share of the global limit is ~ 1.5 t.' },
        { level: 'exceeded', name: 'Biosphere integrity', state: 'Severely exceeded', body: 'Agricultural land use, dominated in this participant\'s case by dairy and animal-feed crops, is the primary global driver.' },
        { level: 'exceeded', name: 'Nitrogen flow', state: 'Severely exceeded', body: 'Animal agriculture is the leading source of reactive nitrogen pollution. This participant\'s high dairy and animal-protein intake contributes.' },
        { level: 'exceeded', name: 'Phosphorus flow', state: 'Exceeded', body: 'Phosphate fertiliser is a finite resource. Eutrophication from runoff drives Baltic/Mediterranean dead zones.' },
        { level: 'exceeded', name: 'Land-system change', state: 'Exceeded', body: 'Tropical forest loss for soy, palm, beef. The dairy share of this participant\'s diet is indirectly linked via feed-soy imports.' },
        { level: 'warn', name: 'Freshwater use', state: 'Approaching limit', body: '~ 70 % of global freshwater goes to agriculture. The participant\'s 1,420 m³ / year is mostly virtual water from imports.' },
        { level: 'warn', name: 'Novel entities (pesticides, microplastics)', state: 'Exceeded', body: 'Pesticide load from non-organic produce, plastic from packaging. Not measured in SEQ v1.' },
        { level: 'warn', name: 'Ocean acidification', state: 'Within limits, worsening', body: 'Linked to climate change. The participant\'s diet contributes via overall CO₂.' },
        { level: 'safe', name: 'Stratospheric ozone', state: 'Recovering', body: 'Food\'s contribution is minor. One of the few global success stories.' },
      ],
      eatLancetRows: [
        { name: 'Vegetables', bar: 'good', barWidth: 68, refLeft: 75, val: '270 / 300 g' },
        { name: 'Fruit', bar: 'good', barWidth: 47, refLeft: 50, val: '190 / 200 g' },
        { name: 'Whole grains', bar: 'bad', barWidth: 12, refLeft: 60, val: '~ 50 / 230 g' },
        { name: 'Legumes / plant proteins', bar: 'bad', barWidth: 8, refLeft: 18, val: '~ 15 / 75 g' },
        { name: 'Nuts and seeds', bar: 'warn', barWidth: 8, refLeft: 12, val: '~ 12 / 50 g' },
        { name: 'Dairy', bar: 'bad', barWidth: 75, refLeft: 62, val: '~ 300 / 250 g' },
        { name: 'Red meat', bar: 'bad', barWidth: 32, refLeft: 7, val: '~ 65 / 14 g' },
        { name: 'Poultry, eggs', bar: 'warn', barWidth: 25, refLeft: 25, val: '~ 50 / 58 g' },
        { name: 'Fish', bar: 'bad', barWidth: 0, refLeft: 14, val: '0 / 28 g' },
      ],
      esgMatrix: [
        {
          row: 'Environmental',
          cells: [
            { rating: 'bad', ratingLabel: 'Concern', title: 'Carbon and land use', body: '2.1 t CO₂e / year is above the 1.5 °C-compatible budget. Dairy and red meat dominate. Land use is 60 % over EAT-Lancet planetary diet reference.' },
            { rating: 'warn', ratingLabel: 'Mixed', title: 'Water and pollution', body: 'Virtual water from coffee, dairy, and meat is substantial. Pesticide and antibiotic exposure are not measured but inferable from non-organic share.' },
            { rating: 'warn', ratingLabel: 'Mixed', title: 'Biodiversity and ecosystems', body: 'Beef and dairy linked to feed-soy expansion (Amazon, Cerrado). Local vegetables and absence of fish are positive contributions.' },
          ],
        },
        {
          row: 'Social',
          cells: [
            { rating: 'bad', ratingLabel: 'Concern', title: 'Labour conditions in supply chain', body: 'Coffee (Ethiopia, Brazil, Vietnam) and cocoa (Ivory Coast, Ghana) involve documented poor working conditions, low wages, and child labour. Tomato, banana, palm-oil supply chains similar.' },
            { rating: 'warn', ratingLabel: 'Mixed', title: 'Animal welfare', body: 'Swiss-produced meat and dairy benefit from strong animal-welfare laws (Tierschutzgesetz). Imported animal products often produced under weaker standards.' },
            { rating: 'warn', ratingLabel: 'Mixed', title: 'Food sovereignty and culture', body: 'Heavy reliance on imports (~ 52 % by weight) means Swiss food sovereignty is partial. Cultural food traditions (cheese, bread) are intact and strong.' },
          ],
        },
        {
          row: 'Governance',
          cells: [
            { rating: 'good', ratingLabel: 'Strong', title: 'Domestic regulatory framework', body: 'Switzerland has strict food-safety, animal-welfare, and labelling rules. Bio Suisse, IP-Suisse, Demeter labels offer high transparency for the consumer.' },
            { rating: 'warn', ratingLabel: 'Mixed', title: 'Supply-chain transparency', body: 'For imports, transparency depends on product. Coffee and chocolate have well-established fair-trade systems. Other imports are harder to trace.' },
            { rating: 'bad', ratingLabel: 'Concern', title: 'Influence of food industry', body: 'Marketing of processed meats and sweetened products to children and adults. Lobbying around sugar tax, meat labelling. Not measured here; mentioned for completeness.' },
          ],
        },
      ],
      supplyChain: [
        { category: 'Beef', origin: 'CH, DE, AR', issue: 'Swiss beef well-regulated; imports linked to Amazon/Cerrado deforestation and large CO₂ footprint.', importShare: '~ 35 %' },
        { category: 'Pork', origin: 'CH, EU', issue: 'Mostly EU; intensive systems with antibiotic use concerns. Swiss pork has stronger welfare standards.', importShare: '~ 10 %' },
        { category: 'Processed meat', origin: 'CH, DE, IT, ES', issue: 'Often industrial. Salt, nitrites, ultra-processed. Linked to cardiovascular and cancer risk.', importShare: '~ 30 %' },
        { category: 'Dairy', origin: 'CH (mostly)', issue: 'Swiss dairy benefits from local production, but feed concentrate (soybean meal) often comes from Brazil/Argentina.', importShare: '~ 5 %' },
        { category: 'Hard cheese', origin: 'CH', issue: 'Swiss strength. Strong cultural and economic value. Footprint per kg is high (~ 8 kg CO₂e/kg) but per portion modest.', importShare: '~ 5 %' },
        { category: 'Coffee', origin: 'BR, VN, ET, CO', issue: 'Tropical land use, low wages for growers, fair-trade share only ~ 10 % of global market. Capsules add waste.', importShare: '100 %' },
        { category: 'Bananas (fruit)', origin: 'EC, CR, CO, PE', issue: 'Pesticide-intensive monoculture. Health issues for workers. Female-headed-household concerns. Improving with EU regulation.', importShare: '100 %' },
        { category: 'Citrus (fruit)', origin: 'ES, IT, MA', issue: 'Spanish citrus involves migrant labour issues, water-stressed regions (Murcia). Moroccan citrus: water and worker concerns.', importShare: '~ 95 %' },
        { category: 'Tomatoes (off-season)', origin: 'ES, MA, NL', issue: 'Spanish "plastic sea" greenhouses near Almería: migrant workers in precarious conditions. Dutch greenhouses: high energy.', importShare: '~ 80 % off-season' },
        { category: 'Wheat (bread, pasta)', origin: 'CH, DE, FR, IT', issue: 'Mostly EU/CH. Limited concerns. Some pesticide questions for non-organic.', importShare: '~ 45 %' },
        { category: 'Rice', origin: 'IT, IN, TH', issue: 'High water use, methane emissions from paddies. Asian imports involve labour and water-equity issues.', importShare: '100 %' },
        { category: 'Soy-based products (if eaten)', origin: 'BR, AR, US', issue: 'Even when destined for direct human consumption, supply chains intersect with Cerrado deforestation. Look for European or Swiss soy.', importShare: '~ 100 %' },
        { category: 'Vegetables (season-dependent)', origin: 'CH, EU', issue: 'Strong seasonal sourcing improves dramatically. Out-of-season EU greenhouse and Spanish imports are the weak point.', importShare: '~ 50 %' },
      ],
      labourCards: [
        {
          eyebrow: 'Issue 8.1',
          title: 'Child labour in cocoa, coffee, and other supply chains',
          paragraphs: [
            'The ILO estimates ~ 160 million children in child labour globally, with ~ 70 % of them in agriculture. Cocoa from West Africa is the textbook case: ~ 1.5 million children working in Ivorian and Ghanaian cocoa, often performing hazardous tasks. Coffee, tobacco, palm oil, and seafood supply chains have similar issues. Swiss consumers contribute to this when chocolate, coffee, and processed-food ingredients come from non-certified supply chains.',
            'For this participant: 4 cups of coffee a day means ~ 5 kg of coffee per year. If the standard non-fair-trade share applies, ~ 4.5 kg of that comes from supply chains with documented labour-condition concerns. Chocolate and snack consumption is not in SEQ v1 but would amplify the issue.',
          ],
        },
        {
          eyebrow: 'Issue 8.2',
          title: 'Modern slavery in seafood',
          paragraphs: [
            'Thai shrimp, much of Southeast Asian seafood processing, and parts of West African fisheries have documented modern-slavery cases. The 2014 Guardian investigation into Thai shrimp suppliers feeding European supermarkets was a wake-up call but conditions have improved unevenly. For the participant: no fish reported, which means low direct contribution, but the issue is structural and applies to anyone whose diet includes industrial seafood.',
          ],
        },
        {
          eyebrow: 'Issue 8.3',
          title: 'Migrant labour in European agriculture',
          paragraphs: [
            'Spanish strawberry farms, Italian tomato fields, Greek vegetable production, German asparagus harvests: substantial parts of European agriculture depend on seasonal migrant labour, often from North Africa, Eastern Europe, or sub-Saharan Africa. Conditions range from acceptable to severely exploitative. The 2020 EU-wide reporting on "ghetto" agricultural settlements in southern Italy is one well-documented case.',
            'For the participant: imported off-season tomatoes, peppers, citrus, and berries during winter all touch these supply chains. SEQ does not measure season or origin, so this is structural inference.',
          ],
        },
        {
          eyebrow: 'Issue 8.4',
          title: 'Living wages for farmers',
          paragraphs: [
            "Globally, smallholder farmers (who produce a third of the world's food) often live below the poverty line, even when feeding the world. Coffee growers in Ethiopia receive a tiny fraction of the price a Swiss consumer pays for a cup. Banana farm workers in Ecuador earn well below local living wages. Fair-trade certification helps but covers only ~ 10 % of global coffee production. Direct-trade and origin-specific schemes do better.",
          ],
        },
        {
          eyebrow: 'Issue 8.5',
          title: 'Gender and land',
          paragraphs: [
            "Women produce roughly 50 % of the world's food but own less than 20 % of the land they work on (FAO). Climate change disproportionately affects women in food systems, since they are often responsible for water collection, household nutrition, and subsistence farming. Investment in women smallholders is one of the highest-leverage interventions in global development.",
          ],
        },
        {
          eyebrow: 'Issue 8.6',
          title: 'Indigenous land rights',
          paragraphs: [
            'Expansion of soy, beef, and palm oil into tropical forests has direct consequences for indigenous communities. The Amazon, Cerrado, Chaco, and Indonesian rainforests all see persistent conflicts between agricultural expansion and indigenous land claims. The Swiss-Mercosur trade agreement debate (currently active) touches this directly.',
          ],
        },
        {
          eyebrow: 'Issue 8.7',
          title: 'Swiss farmers',
          paragraphs: [
            "Worth keeping in view: the human story isn't only foreign. Swiss agriculture employs ~ 150,000 people, of whom many face mental-health challenges, succession problems, and economic pressure. Average Swiss farm income is well below the median wage. The Swiss farmers' protests of 2024 reflect deep tensions. A consumer-facing sustainability story should not appear to celebrate distant production while ignoring local farmers.",
          ],
        },
      ],
      animalWelfareTable: [
        { name: 'Dairy cow (share of)', count: '~ 0.14', note: "One dairy cow produces ~ 7,000 L milk per year; this participant consumes the equivalent of ~ 1 / 7 of a cow's annual output." },
        { name: 'Chickens (broiler)', count: '~ 12', note: 'Poultry meat ~ 14 kg / year, ~ 1.2 kg meat per bird.' },
        { name: 'Laying hens (share of)', count: '~ 0.5', note: '~ 130 eggs / year from a single hen; participant eats ~ 65.' },
        { name: 'Pigs (share of)', count: '~ 0.3', note: 'One pig yields ~ 60 kg pork; participant eats ~ 18 kg.' },
        { name: 'Cattle for beef (share of)', count: '~ 0.04', note: 'One beef animal yields ~ 200 kg; participant eats ~ 8 kg.' },
        { name: 'Fish', count: '0', note: 'None reported.' },
        { name: 'Total animals affected directly', count: '~ 13', note: 'Dominated by poultry, since each broiler chicken is one animal regardless of weight.' },
      ],
      sdgTiles: [
        { num: 'SDG 2', title: 'Zero Hunger', body: 'Heavy meat and dairy use indirect food calories that could feed more people. Switching to plant proteins increases system-level food efficiency.' },
        { num: 'SDG 3', title: 'Good Health and Well-being', body: 'Nutrition score 76 / 100 indicates moderate alignment. Plant-protein gap and processed-meat surplus are health concerns.' },
        { num: 'SDG 5', title: 'Gender Equality', body: 'Imported food often comes from systems where women are underpaid and underrepresented in land ownership. Fair-trade certifications partly address this.' },
        { num: 'SDG 6', title: 'Clean Water and Sanitation', body: "Diet's water footprint of ~ 1,420 m³ / year touches water-scarce regions abroad (Spain, Morocco, Ethiopia)." },
        { num: 'SDG 8', title: 'Decent Work', body: 'Coffee, banana, citrus, tomato, processed-food labour conditions in supply chains are mixed. Local Swiss products score well.' },
        { num: 'SDG 12', title: 'Responsible Consumption', body: 'Heavy import share and food-waste pattern (Swiss-typical) suggest room to improve. Seasonal eating not yet measured.' },
        { num: 'SDG 13', title: 'Climate Action', body: '2.1 t CO₂e from diet is above the 1.5 °C-compatible budget. Dairy and meat are the levers.' },
        { num: 'SDG 14', title: 'Life Below Water', body: 'No fish consumed; direct ocean impact is near zero. Indirect impact through palm-oil-driven coastal change and runoff remains.' },
        { num: 'SDG 15', title: 'Life on Land', body: 'Dairy and beef supply chains link to tropical deforestation. Coffee touches the same issue.' },
        { num: 'SDG 17', title: 'Partnerships for the Goals', body: 'Cross-border food systems demand cross-border governance: fair-trade systems, ILO standards, EU Deforestation Regulation.' },
      ],
      swissMetrics: [
        { label: 'Swiss food self-sufficiency', value: '52', unit: '%', status: 'warn', detail: 'Approx. share of Swiss food produced domestically (by calorie). Imports cover the rest.' },
        { label: 'Swiss per capita food waste', value: '330', unit: 'kg / yr', status: 'bad', detail: 'One of the highest per-capita waste rates in the world. ~ 1/3 of food bought is thrown away.' },
        { label: 'Swiss meat consumption', value: '~ 50', unit: 'kg / yr', status: 'warn', detail: 'Above the global average, below US average. Slowly declining since 2010.' },
        { label: 'Organic / Bio market share', value: '11', unit: '%', status: 'good', detail: 'Among the highest in Europe by retail share. Bio Suisse Knospe is the leading label.' },
      ],
      swissLabels: [
        { label: 'Bio Suisse Knospe', scope: 'Organic, mostly domestic', focus: 'Strict organic standards; no synthetic pesticides, high animal welfare, biodiversity rules.' },
        { label: 'IP-Suisse', scope: 'Integrated production', focus: 'Sustainable agriculture, biodiversity, animal welfare. Less strict than Bio but covers more area.' },
        { label: 'Demeter', scope: 'Biodynamic', focus: 'Strictest organic standard, plus biodynamic principles. Small market share.' },
        { label: 'Naturafarm', scope: "Coop's animal welfare", focus: 'Above-legal animal welfare, especially poultry and pork.' },
        { label: 'Swiss Garantie', scope: 'Origin', focus: 'Swiss origin guarantee. Says nothing about production method.' },
        { label: 'AOP / IGP', scope: 'Regional speciality', focus: 'Protected origin (Gruyère AOP, Emmentaler AOP, etc.). Sustainability indirect via tradition.' },
        { label: 'Max Havelaar / Fairtrade', scope: 'Imports', focus: 'Fair-trade certification for coffee, cocoa, sugar, bananas, etc.' },
        { label: 'MSC / ASC', scope: 'Seafood', focus: 'Sustainable fisheries and aquaculture, respectively.' },
      ],
      wasteMetrics: [
        { label: 'Food waste — Swiss avg.', value: '~ 330', unit: 'kg / household / yr', status: 'warn', detail: 'If the participant fits the average, their personal waste is roughly half this. SEQ does not measure.' },
        { label: 'Packaging waste — Swiss avg.', value: '~ 95', unit: 'kg / person / yr', status: 'warn', detail: 'Of which ~ 35 kg plastic. Substantial share is food packaging. Reusable systems still rare.' },
        { label: 'Seasonal eating — share', value: '?', unit: '', status: 'warn', detail: 'SEQ does not measure seasonality. Could be added as a v2 self-report question.' },
      ],
      scenarios: [
        { name: 'Scenario A: Add legumes 3× / week', delta: '−180 kg CO₂e / yr', pill: 'good', pillLabel: 'Easy win', body: 'Replacing 3 meat meals per week with legume-based meals. Both nutrition and sustainability scores improve. Cost-saving for most Swiss households. The single largest synergy between health and planet in this participant\'s diet.' },
        { name: 'Scenario B: Swap white bread for whole-grain', delta: '−40 kg CO₂e / yr', pill: 'good', pillLabel: 'Easy win', body: 'Small climate gain but large nutritional gain (fibre, micronutrients). Whole grains require less processing energy and offer higher per-area yield of nutrition. Cheap to implement.' },
        { name: 'Scenario C: Reduce coffee from 4 to 2 cups', delta: '−160 kg CO₂e / yr', pill: 'warn', pillLabel: 'Cultural pushback', body: 'Half the coffee, half the footprint. Switching to a moka pot or filter from a capsule machine adds another 30 % reduction. Worth discussing whether SEQ should surface this; coffee is deeply embedded in Swiss culture and a moralising tone here would not land well.' },
        { name: 'Scenario D: Switch to seasonal vegetables and fruit only', delta: '−180 kg CO₂e / yr', pill: 'warn', pillLabel: 'Requires habits', body: 'Significant gain. Requires the consumer to think in seasons, which is a learnable habit. Schools and food retailers can help via seasonal calendars and visible labelling.' },
        { name: 'Scenario E: Halve processed meat, add fish 1× / week', delta: '−130 kg CO₂e / yr', pill: 'good', pillLabel: 'Nutrition gain', body: 'Halving processed meat reduces cancer/CVD risk and carbon. Adding 1 weekly fish meal (preferably MSC-certified or sustainable lake fish) addresses the omega-3 gap. Net climate impact slightly favourable.' },
        { name: 'Scenario F: Choose Swiss origin for all animal products', delta: '~ 0 kg CO₂e (varies)', pill: 'good', pillLabel: 'Welfare gain', body: 'Climate impact roughly neutral (Swiss livestock has its own emissions). Animal welfare and labour conditions improve substantially. Land-use and biodiversity link to feed-soy imports may persist.' },
        { name: 'Scenario G: EAT-Lancet planetary diet', delta: '−920 kg CO₂e / yr', pill: 'good', pillLabel: 'Maximum impact', body: 'Adopting the full planetary diet (mostly plants, modest dairy and animal protein, lots of legumes and whole grains, fish 1× / week) would cut diet-related emissions by ~ 44 % for this participant. Health benefits parallel. Large lifestyle adjustment; not realistic as a single step.' },
        { name: 'Scenario H: Eliminate household food waste', delta: '−400 kg CO₂e / yr', pill: 'warn', pillLabel: 'Behaviour-heavy', body: 'If the participant matches the Swiss average for waste, halving it would deliver a substantial climate gain with zero compromise on what they eat. Requires planning, shopping, and storage habits.' },
      ],
      brainstorm: [
        {
          eyebrow: 'Question 1',
          title: 'Should the participant-facing report include sustainability content at all?',
          body: `<p>Arguments for: it's important, evidence-based, and aligned with broader BFH and SGE mission.</p>
<p>Arguments against: it dilutes the core nutritional message, risks moralising, and may turn participants off ("now they want me to feel bad about my coffee").</p>
<ul>
  <li>Could it be opt-in? A toggle for those interested?</li>
  <li>Could it be implicit, embedded in nutrition advice rather than its own section?</li>
  <li>Should it appear only in the professional reports (dietician, health, teacher) and not the consumer one?</li>
</ul>`,
        },
        {
          eyebrow: 'Question 2',
          title: 'How much new data should we collect to support sustainability scoring?',
          body: `<p>SEQ v1 measures frequency only. To do sustainability well, we would want:</p>
<ul>
  <li>Origin / label awareness (do you buy Swiss / Bio?)</li>
  <li>Seasonality (do you eat seasonally?)</li>
  <li>Food waste (how much do you throw away?)</li>
  <li>Snacks, oils, sweets (sustainability impact is real)</li>
  <li>Coffee type (capsule, filter, espresso?)</li>
</ul>
<p>Each new question costs participant time and risks drop-off. The 67-question SEQ is already substantial. What is the smallest set of additions that unlocks meaningful sustainability inference?</p>`,
        },
        {
          eyebrow: 'Question 3',
          title: 'Whose framing of sustainability do we adopt?',
          body: `<p>Different frameworks emphasise different things:</p>
<ul>
  <li>EAT-Lancet: scientific, quantitative, focused on planet within boundaries</li>
  <li>FAO Sustainable Diets: broader, includes culture and economic fairness</li>
  <li>SDGs: political, broad, sometimes too abstract</li>
  <li>ESG: corporate, useful for institutional partners</li>
  <li>Planetary Boundaries: Earth-system science, scientifically robust but abstract for consumers</li>
</ul>
<p>SEQ could pick one as primary or layer several. What is most useful for our partner audiences (schools, hospitals, public health)?</p>`,
        },
        {
          eyebrow: 'Question 4',
          title: 'How do we handle the "blame the consumer" trap?',
          body: `<p>Individual diet choices matter but are nested in systems (food retail, marketing, regulation, agricultural policy). A report that points only at the individual risks reinforcing the dishonest framing that "consumers should fix this." How do we communicate sustainability honestly without moralising?</p>
<ul>
  <li>Could we include a parallel "what the system could do" section?</li>
  <li>Could we link to advocacy or policy suggestions?</li>
  <li>Should the institutional reports (school, hospital) carry the policy framing while the consumer report stays behaviour-focused?</li>
</ul>`,
        },
        {
          eyebrow: 'Question 5',
          title: 'How do we surface the human-and-labour dimension respectfully?',
          body: `<p>Talking about child labour in cocoa or migrant workers in Spanish strawberries is uncomfortable for the consumer. It can also feel patronising ("the European tells the consumer about the Global South"). Yet ignoring it is dishonest. How do we strike the right tone?</p>
<ul>
  <li>Surface it through neutral facts rather than moral framing</li>
  <li>Pair it with action options (fair-trade labels, certifications)</li>
  <li>Involve partner organisations (Helvetas, Fastenaktion) in the writing</li>
</ul>`,
        },
        {
          eyebrow: 'Question 6',
          title: 'Where does animal welfare fit?',
          body: `<p>Switzerland has strong welfare legislation. Swiss consumers care about animal welfare more than most. Yet imported animal products are often produced under conditions illegal here. Should SEQ explicitly call out this asymmetry? Or treat it as an origin / label question?</p>`,
        },
        {
          eyebrow: 'Question 7',
          title: 'What does a "good" sustainability score look like?',
          body: `<p>The 52 / 100 score in this report is a placeholder. Building a real composite score requires deciding what fraction of the score is climate, what fraction is land, biodiversity, labour, animal welfare, etc. These decisions are political, not technical.</p>
<ul>
  <li>Should we have one score or multiple sub-scores?</li>
  <li>Should we benchmark to EAT-Lancet (planetary diet = 100), or to Swiss average (Swiss avg = 50)?</li>
  <li>Should the score adapt to context (single adult vs. family with children)?</li>
</ul>`,
        },
        {
          eyebrow: 'Question 8',
          title: 'What is the partner ecosystem for this work?',
          body: `<p>Who else cares about Swiss food sustainability and might want to partner or use SEQ data?</p>
<ul>
  <li>BAFU (Federal Office for the Environment)</li>
  <li>BLV (Federal Food Safety and Veterinary Office)</li>
  <li>Agroscope (federal research)</li>
  <li>SGE / SSN (Swiss Society for Nutrition)</li>
  <li>Bio Suisse, IP-Suisse, Demeter</li>
  <li>WWF Switzerland</li>
  <li>Helvetas, Fastenaktion (development NGOs)</li>
  <li>Migros and Coop sustainability teams</li>
  <li>ETH and EPFL sustainability research</li>
  <li>Eaternity (Swiss food carbon-footprint startup)</li>
</ul>`,
        },
      ],
      caveats: [
        { eyebrow: 'Caveat 15.1', title: 'All numbers in this report are estimates', body: 'The carbon, land, water, and biodiversity figures rely on Life-Cycle Assessment averages. Real footprints vary by farm, season, supplier, year, weather, and feed source. Reasonable single-source uncertainty is ±20 %; for some items (especially imported tropical products) it can be ±40 % or more. The rank order between food groups is robust; the absolute numbers are not.' },
        { eyebrow: 'Caveat 15.2', title: 'SEQ does not measure portion size', body: 'SEQ measures frequency only. Two people eating chicken "weekly" might eat 100 g or 400 g. Sustainability scoring becomes more reliable with portion data; food-diary tools (FFQ-extended, 24h recall) are gold standard but much more burdensome on participants.' },
        { eyebrow: 'Caveat 15.3', title: 'Indirect effects matter and are hard to measure', body: 'A Swiss dairy farmer feeding their cows imported soy concentrate links your morning yoghurt to Brazilian deforestation. This kind of indirect chain is real but invisible to most consumers and not currently in SEQ. Capturing it requires partnership with supply-chain transparency initiatives.' },
        { eyebrow: 'Caveat 15.4', title: 'Behavior change is harder than the numbers suggest', body: 'Most "what if" scenarios in Section 13 assume the participant simply changes their diet. In reality, dietary change is one of the hardest behaviour changes in public health, often slower than smoking cessation. A sustainability score that produces guilt without supporting change is counterproductive.' },
        { eyebrow: 'Caveat 15.5', title: 'Sustainability is contested', body: 'What counts as "sustainable" is partly a values question. A regenerative beef farm in the Swiss Alps and a Brazilian feedlot both produce beef but their sustainability profiles differ wildly. A pure climate metric would treat them similarly; a broader metric would not. This report uses standard LCA conventions; reasonable people may disagree.' },
        { eyebrow: 'Caveat 15.6', title: 'This is a brainstorming artefact, not a publication', body: 'All figures should be cross-checked, ideally with Agroscope or BAFU sources, before publication or external use. Some of the supply-chain attribution is structural inference rather than data-backed. The frameworks (EAT-Lancet, planetary boundaries, ESG, SDGs) are described at a working level, not in scholarly depth.' },
      ],
    };
  },
};
</script>

<style scoped>
.results9-wrap {
  --ink: #1A1A1A;
  --ink-soft: #2D2D2D;
  --ink-mute: #6B6B6B;
  --ink-dim: #9C9C9C;
  --rule: #DDDDDD;
  --paper: #FAFAF7;
  --card: #FFFFFF;
  --warm: #F2EDE4;
  --good: #2D8659;
  --good-bg: #B8D8C5;
  --warn: #E8A33D;
  --warn-bg: #F5D9A8;
  --bad: #C8392E;
  --bad-bg: #ECB8B3;
  --neutral: #4A6FA5;
  --neutral-bg: #C8D4E6;
  --accent: #4A8B6E;
  --accent-bg: #C5D9CD;
  --earth: #8B6B47;
  --earth-bg: #D9C9B5;
  --mono: 'JetBrains Mono', 'SF Mono', Consolas, monospace;

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink);
  background: var(--paper);
  min-height: 100%;
}
.results9-wrap *,
.results9-wrap *::before,
.results9-wrap *::after {
  box-sizing: border-box;
}

/* TOPBAR */
.topbar {
  background: var(--ink);
  color: white;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  padding: 12px 32px;
}
.topbar-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1100px;
  margin: 0 auto;
}
.brand { display: flex; align-items: center; gap: 12px; }
.brand-mark { width: 18px; height: 18px; background: var(--accent); }
.brand-text { font-family: 'Inter', sans-serif; font-weight: 600; font-size: 14px; }
.brand-text small {
  display: block;
  font-weight: 400;
  font-size: 10px;
  letter-spacing: 0.04em;
  color: var(--ink-dim);
  margin-top: -2px;
}
.topbar-meta { font-size: 11px; }

/* HERO */
.hero {
  background: var(--card);
  padding: 36px 32px;
  border-bottom: 1px solid var(--rule);
}
.hero-inner { max-width: 1100px; margin: 0 auto; }
.hero-eyebrow {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 12px;
}
.hero-title {
  font-size: 38px;
  font-weight: 600;
  line-height: 1.12;
  margin: 0 0 14px 0;
  letter-spacing: -0.01em;
}
.hero-title em { font-style: italic; color: var(--accent); }
.hero-sub {
  color: var(--ink-mute);
  font-size: 15px;
  max-width: 760px;
  margin: 0;
}

/* META */
.meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
  background: var(--warm);
}
.meta-cell {
  padding: 14px 20px;
  border-right: 1px solid var(--rule);
  font-size: 12px;
}
.meta-cell:last-child { border-right: none; }
.meta-label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 4px;
}
.meta-value { font-weight: 500; color: var(--ink); }

/* TOC */
.toc {
  background: var(--card);
  padding: 24px 32px;
  border-bottom: 1px solid var(--rule);
}
.toc-inner { max-width: 1100px; margin: 0 auto; }
.toc-title {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 14px;
}
.toc-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px 24px;
  font-size: 13px;
}
.toc-item {
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 8px;
  padding: 4px 0;
}
.toc-num { font-family: var(--mono); font-size: 11px; color: var(--ink-mute); }
.toc-item-title { color: var(--ink); }

/* SECTION */
.section { padding: 40px 32px; border-bottom: 1px solid var(--rule); }
.section.warm { background: var(--warm); }
.section.accent { background: var(--accent-bg); }
.section.earth { background: var(--earth-bg); }
.section-inner { max-width: 1100px; margin: 0 auto; }
.section-header {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 24px;
  margin-bottom: 28px;
}
.section-num {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  color: var(--ink-dim);
  padding-top: 8px;
  border-top: 1px solid var(--ink);
}
.section-title {
  font-size: 26px;
  font-weight: 600;
  margin: 0 0 8px 0;
  line-height: 1.2;
  letter-spacing: -0.01em;
}
.section-title em { font-style: italic; color: var(--accent); }
.section-lede {
  color: var(--ink-mute);
  font-size: 14px;
  max-width: 780px;
  margin: 0;
}
.section-note {
  margin-top: 16px;
  color: var(--ink-mute);
  font-size: 13px;
  max-width: 780px;
}

/* CARDS */
.card {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 22px 24px;
  margin-bottom: 14px;
}
.card-eyebrow {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-dim);
  margin-bottom: 4px;
}
.card-title {
  font-size: 19px;
  font-weight: 600;
  margin-bottom: 4px;
  letter-spacing: -0.005em;
}
.card-sub {
  color: var(--ink-mute);
  font-size: 13px;
  margin-bottom: 18px;
}
.card p { color: var(--ink-soft); margin: 0 0 12px 0; }
.card p:last-child { margin-bottom: 0; }
.card-paragraph--note {
  margin-top: 18px;
  color: var(--ink-mute);
  font-size: 13px;
}

/* SUMMARY METRIC TILES */
.metric-grid-5 {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 14px;
}
.metric-grid-4 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 14px;
}
.metric-grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 14px;
}
.metric {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 14px 16px;
}
.metric-label {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 4px;
}
.metric-value {
  font-size: 26px;
  font-weight: 600;
  line-height: 1.05;
}
.metric-value .unit {
  font-size: 12px;
  font-weight: 400;
  color: var(--ink-mute);
  margin-left: 4px;
}
.metric-value.good { color: var(--good); }
.metric-value.warn { color: var(--warn); }
.metric-value.bad { color: var(--bad); }
.metric-detail {
  font-size: 11px;
  color: var(--ink-mute);
  margin-top: 6px;
  line-height: 1.5;
}

/* COMPARISON BARS */
.compare-row {
  display: grid;
  grid-template-columns: 180px 1fr 120px;
  gap: 14px;
  padding: 10px 0;
  border-bottom: 1px solid var(--rule);
  align-items: center;
  font-size: 13px;
}
.compare-row:last-child { border-bottom: none; }
.compare-row.head {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  border-bottom: 1px solid var(--ink);
}
.compare-name { font-weight: 500; }
.compare-bar-wrap {
  background: var(--warm);
  height: 14px;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
}
.compare-bar {
  height: 100%;
  background: var(--accent);
  border-radius: 2px;
}
.compare-bar.bad { background: var(--bad); }
.compare-bar.warn { background: var(--warn); }
.compare-bar.good { background: var(--good); }
.compare-ref {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--ink);
}
.compare-val {
  font-family: var(--mono);
  font-size: 12px;
  text-align: right;
  font-weight: 600;
}

/* ESG MATRIX */
.esg-matrix {
  display: grid;
  grid-template-columns: 100px repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}
.esg-header {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  padding: 8px 12px;
  text-align: center;
  background: var(--warm);
}
.esg-header.row { text-align: left; }
.esg-cell {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 12px 14px;
  font-size: 12px;
  line-height: 1.5;
  position: relative;
}
.esg-cell-rating {
  font-family: var(--mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 2px;
  display: inline-block;
  margin-bottom: 6px;
}
.esg-cell-rating.good { background: var(--good-bg); color: var(--good); }
.esg-cell-rating.warn { background: var(--warn-bg); color: var(--warn); }
.esg-cell-rating.bad { background: var(--bad-bg); color: var(--bad); }
.esg-cell-rating.neutral { background: var(--neutral-bg); color: var(--neutral); }
.esg-cell-title {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
}
.esg-cell-body { color: var(--ink-soft); }

/* PILLS */
.pill {
  display: inline-block;
  font-family: var(--mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 2px;
  white-space: nowrap;
}
.pill.good { background: var(--good-bg); color: var(--good); }
.pill.warn { background: var(--warn-bg); color: var(--warn); }
.pill.bad { background: var(--bad-bg); color: var(--bad); }
.pill.flat { background: var(--rule); color: var(--ink-mute); }
.pill.neutral { background: var(--neutral-bg); color: var(--neutral); }

/* TABLE */
table.data {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
table.data.table-spaced { margin-top: 8px; }
table.data th {
  text-align: left;
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  padding: 8px 12px;
  border-bottom: 1px solid var(--ink);
  font-weight: 500;
}
table.data td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--rule);
  vertical-align: top;
}
table.data tr:last-child td { border-bottom: 1px solid var(--ink); }
table.data .num { font-family: var(--mono); text-align: right; }
table.data .name { font-weight: 500; }

/* PLANETARY BOUNDARIES */
.boundary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.boundary-card {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 14px 16px;
  border-left: 4px solid var(--rule);
}
.boundary-card.exceeded { border-left-color: var(--bad); }
.boundary-card.warn { border-left-color: var(--warn); }
.boundary-card.safe { border-left-color: var(--good); }
.boundary-name {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
}
.boundary-state {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.boundary-card.exceeded .boundary-state { color: var(--bad); }
.boundary-card.warn .boundary-state { color: var(--warn); }
.boundary-card.safe .boundary-state { color: var(--good); }
.boundary-body { font-size: 12px; color: var(--ink-soft); line-height: 1.5; }

/* SDG GRID */
.sdg-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.sdg-tile {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 12px 14px;
  text-align: left;
}
.sdg-num {
  font-family: var(--mono);
  font-weight: 600;
  font-size: 11px;
  color: var(--neutral);
  margin-bottom: 4px;
  letter-spacing: 0.04em;
}
.sdg-title {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: 1.3;
}
.sdg-body {
  font-size: 11px;
  color: var(--ink-mute);
  line-height: 1.5;
}

/* SUPPLY CHAIN */
.supply-row {
  display: grid;
  grid-template-columns: 140px 100px 1fr 80px;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--rule);
  font-size: 13px;
  align-items: center;
}
.supply-row:last-child { border-bottom: none; }
.supply-row.head {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  border-bottom: 1px solid var(--ink);
}
.supply-issue { font-size: 12px; color: var(--ink-soft); }

/* BRAINSTORM */
.brainstorm-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.brainstorm-card {
  background: var(--card);
  border: 1px solid var(--rule);
  border-left: 4px solid var(--accent);
  padding: 18px 22px;
}
.brainstorm-eyebrow {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 8px;
}
.brainstorm-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 10px;
}
.brainstorm-body {
  font-size: 13px;
  color: var(--ink-soft);
  line-height: 1.6;
}
.brainstorm-body :deep(p) { margin: 0 0 8px 0; }
.brainstorm-body :deep(p:last-child) { margin-bottom: 0; }
.brainstorm-body :deep(ul) { padding-left: 18px; margin: 8px 0; }
.brainstorm-body :deep(li) { margin-bottom: 4px; }

/* SCENARIOS */
.scenario-card {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 20px 22px;
  margin-bottom: 12px;
}
.scenario-header {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 16px;
  align-items: baseline;
  margin-bottom: 12px;
}
.scenario-name { font-size: 16px; font-weight: 600; }
.scenario-delta {
  font-family: var(--mono);
  font-weight: 600;
  font-size: 13px;
  color: var(--good);
}
.scenario-body { color: var(--ink-soft); font-size: 13px; }

/* FOOTER */
.footer {
  background: var(--ink);
  color: white;
  padding: 36px 32px;
  font-size: 12px;
}
.footer-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
}
.footer p {
  margin: 8px 0 0 0;
  color: var(--ink-dim);
  max-width: 600px;
  line-height: 1.6;
}
.footer-meta {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--ink-dim);
  text-align: right;
}
.placeholder-note {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-dim);
  text-align: center;
  margin-top: 16px;
}

/* PRINT */
@media print {
  .results9-wrap { background: white; }
  .topbar { background: white; color: black; }
  .hero, .card, .metric, .boundary-card, .scenario-card, .brainstorm-card, .esg-cell { background: white; }
  .section { padding: 24px; border: none; }
  .no-print { display: none; }
  table.data { font-size: 11px; }
}
</style>
